import { z } from 'zod';

/**
 * The settings the console can edit.
 *
 * `settings` is a jsonb key/value table: the server validates that the value is
 * JSON and nothing more, so the SHAPE of each key is agreed here. Only
 * `temperature.weights` is read by the server today — the rest are stored and
 * shown, waiting for the services that will consume them (noted in the report).
 *
 * These schemas live on the client rather than in `src/shared` because
 * `src/shared` is owned by the server half of this project and was out of scope
 * for this work. `temperature.weights` in particular MUST stay in step with
 * `TemperatureWeights` in `src/server/services/conversation/temperature.ts`;
 * moving it into `src/shared` is the right fix and is listed in the report.
 */

export const temperatureWeightsSchema = z.object({
  fit: z.number().min(0).max(100),
  documents: z.number().min(0).max(100),
  engagement: z.number().min(0).max(100),
  intent: z.number().min(0).max(100),
});
export type TemperatureWeights = z.infer<typeof temperatureWeightsSchema>;

export const DEFAULT_WEIGHTS: TemperatureWeights = {
  fit: 30,
  documents: 30,
  engagement: 25,
  intent: 15,
};

export const knowledgeEntrySchema = z.object({
  id: z.string(),
  question: z.string(),
  answer: z.string(),
  /** Free tags the retrieval step matches on. */
  tags: z.array(z.string()).default([]),
});
export type KnowledgeEntry = z.infer<typeof knowledgeEntrySchema>;
export const knowledgeBaseSchema = z.array(knowledgeEntrySchema);

export const escalationRuleSchema = z.object({
  id: z.string(),
  /** Which signal trips the rule. Mirrors the park reasons the server owns. */
  when: z.enum([
    'asked_for_human',
    'frustrated',
    'ai_stuck',
    'low_confidence',
    'money_question',
    'missing_data',
    'complaint',
    'ai_error',
    'out_of_hours',
  ]),
  enabled: z.boolean(),
  /** Minutes the assistant may keep trying before it parks anyway. */
  graceMinutes: z.number().int().min(0).max(120),
  note: z.string().default(''),
});
export type EscalationRule = z.infer<typeof escalationRuleSchema>;
export const escalationRulesSchema = z.array(escalationRuleSchema);

export const cannedResponseSchema = z.object({
  id: z.string(),
  /** What the operator types after `/`. */
  shortcut: z.string(),
  body: z.string(),
});
export type CannedResponse = z.infer<typeof cannedResponseSchema>;
export const cannedResponsesSchema = z.array(cannedResponseSchema);

const dayHoursSchema = z.object({
  open: z.boolean(),
  /** 24-hour `HH:MM`, in the agency's local time. */
  from: z.string().regex(/^\d{2}:\d{2}$/),
  to: z.string().regex(/^\d{2}:\d{2}$/),
});

export const workingHoursSchema = z.object({
  timezone: z.string(),
  monday: dayHoursSchema,
  tuesday: dayHoursSchema,
  wednesday: dayHoursSchema,
  thursday: dayHoursSchema,
  friday: dayHoursSchema,
  saturday: dayHoursSchema,
  sunday: dayHoursSchema,
});
export type WorkingHours = z.infer<typeof workingHoursSchema>;

const WEEKDAY = { open: true, from: '08:00', to: '18:00' };
const WEEKEND = { open: false, from: '09:00', to: '13:00' };

export const DEFAULT_WORKING_HOURS: WorkingHours = {
  timezone: 'Africa/Nairobi',
  monday: WEEKDAY,
  tuesday: WEEKDAY,
  wednesday: WEEKDAY,
  thursday: WEEKDAY,
  friday: WEEKDAY,
  saturday: { open: true, from: '09:00', to: '13:00' },
  sunday: WEEKEND,
};

/** Approved WhatsApp templates, the only thing sendable once the window closes. */
export const templateSchema = z.object({
  id: z.string(),
  name: z.string(),
  language: z.string(),
  body: z.string(),
});
export type WaTemplate = z.infer<typeof templateSchema>;
export const templatesSchema = z.array(templateSchema);

export const SETTING_KEYS = {
  temperatureWeights: 'temperature.weights',
  knowledge: 'knowledge.entries',
  escalation: 'escalation.rules',
  canned: 'canned.responses',
  workingHours: 'working.hours',
  templates: 'wa.templates',
} as const;
